import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useContext, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { DeliveryClientDetails } from './steppers/DeliveryClientDetails';
import { DeliveryPickupInformation } from './steppers/DeliveryPickupInformation';
import { DeliveryInformation } from './steppers/DeliveryInformation';
import { DeliveryBilling } from './steppers/DeliveryBilling';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  buildDeliveryFormSchema,
  buildDeliveryInformationSchema,
  DeliveryClientDetailsSchema,
  DeliveryPickupInformationSchema,
  DeliveryFormData,
} from './delivery.schema';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import AlertBox from '@/components/AlertBox';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deliveryService } from '@/services/deliveryService';
import { DeliveryCreateRequest, EditIntakeFormRequest, EditWorkOrderRequest } from '@/types/delivery.types';
import { DeliveryDetailResponse } from '@/types/deliveryDetail.types';
import type { GenericErrorResponse } from '@/types/api.types';




// Helper function to parse address string into components
// Helper function to parse address string into components
// Helper function to parse address string into components
// Helper function to parse address string into components safely
const parseAddress = (addressString: string) => {
  if (!addressString) return { line1: '', line2: undefined, city: '', state: '', zip: '' };

  let line1 = addressString;
  let line2: string | undefined = undefined;
  let city = '';
  let state = '';
  let zip = '';

  // Match the end of the string for something resembling: [City], [State] [Zip]
  // Matches optional comma, city (letters/spaces/dots/dashes), optional comma, 2-letter state, and zip.
  const endPattern = /(?:,\s*)?([A-Za-z\s.-]+?)(?:,\s*)?([A-Za-z]{2})\s+([\w\d\s-]{3,})$/i;

  const match = addressString.match(endPattern);
  if (match) {
    city = match[1].trim();
    state = match[2].toUpperCase();
    zip = match[3].trim();

    // The rest of the string before the match is the street address
    line1 = addressString.substring(0, match.index).trim();

    // Clean up trailing commas from the line1
    if (line1.endsWith(',')) {
      line1 = line1.substring(0, line1.length - 1).trim();
    }
  } else {
    // If no clean state/zip match, try splitting by comma as a fallback
    const parts = addressString.split(',').map(p => p.trim());
    if (parts.length >= 3) {
      const lastPart = parts[parts.length - 1];
      const secondLastPart = parts[parts.length - 2];

      const stateZipMatch = lastPart.match(/^([A-Za-z]{2})\s+([\w\d\s-]{3,})$/i);
      if (stateZipMatch) {
        state = stateZipMatch[1].toUpperCase();
        zip = stateZipMatch[2].trim();
        city = secondLastPart;
        line1 = parts.slice(0, parts.length - 2).join(', ').trim();
      }
    }
  }

  // Very rudimentary Line 2 split if Line 1 has a comma left, like "123 Main St, Apt 4"
  if (line1 && line1.includes(',')) {
    const parts = line1.split(',');
    line1 = parts[0].trim();
    line2 = parts.slice(1).join(',').trim();
  }

  return { line1, line2, city, state, zip };
};

// Helper function to safely parse date
const parseDateSafe = (dateString: string | null | undefined, fallback: Date = new Date()): Date => {
  if (!dateString) return fallback;
  try {
    const parsed = new Date(dateString);
    // Check if date is valid
    if (isNaN(parsed.getTime())) {
      console.error(`Invalid date string: ${dateString}`);
      return fallback;
    }
    return parsed;
  } catch (error) {
    console.error(`Error parsing date: ${dateString}`, error);
    return fallback;
  }
};

// Helper function to transform API response to form data
const transformApiResponseToFormData = (apiData: DeliveryDetailResponse): Partial<DeliveryFormData> => {
  const {
    summary,
    clientDetails,
    pickupInformation,
    deliveryInformation,
    billing,
    commonDetails
  } = apiData;

  // Parse addresses
  const pickupAddr = parseAddress(pickupInformation.pickupAddress);
  const deliveryAddr = parseAddress(deliveryInformation.deliveryAddress);

  // Parse dates safely
  const pickupDateTime = parseDateSafe(pickupInformation.pickupDateTime);
  const deliveryDateTime = parseDateSafe(deliveryInformation.deliveryDateTime);
  const returnDate = parseDateSafe(deliveryInformation.returnDate, deliveryDateTime);

  // Extract billing items
  const billingItems = 'billingItems' in billing ? billing.billingItems : [];
  const getBillingAmount = (code: string) => {
    const item = billingItems.find(b => b.code === code);
    return item ? item.amount : 0;
  };

  const totalBillingAmount = 'totalBillingAmount' in billing
    ? billing.totalBillingAmount
    : billingItems.reduce((sum, item) => sum + item.amount, 0);

  // Extract printing job pages if available
  const printingPagesRaw: any = (pickupInformation as any).printing || (commonDetails as any).pages || 0;
  const printingPages = (parseInt(String(printingPagesRaw), 10) || 0) as number;

  return {
    type: 'DELIVERY',
    clientId: summary.clientCode,
    clientName: summary.clientName,
    caseName: clientDetails.caseName ?? undefined,
    fileReferenceNumber: clientDetails.fileReferenceNumber ?? undefined,
    contactId: clientDetails.clientContactId,
    contactPhone: clientDetails.contactPhone ?? undefined,
    contactEmail: clientDetails.contactEmail ?? undefined,
    isPrepaid: commonDetails.isPrepaid,

    // Pickup Information
    pickupPersonName: pickupInformation.pickupName,
    pickupPhone: undefined, // pickupInformation has no pickupPhone field, only pickupDriverPhone
    pickupAddressSameAsClient: false,
    pickupAddressLine1: pickupAddr.line1,
    pickupAddressLine2: pickupAddr.line2 || undefined,
    pickupCity: pickupAddr.city,
    pickupCounty: pickupInformation.pickupCountyId ? String(pickupInformation.pickupCountyId) : '',
    pickupState: pickupAddr.state,
    pickupZip: pickupAddr.zip,
    pickupDate: pickupDateTime,
    pickupType: pickupInformation.pickupType as 'ENVELOPE' | 'BOX' | 'OTHER',
    pickupTypeOther: pickupInformation.pickupTypeOther ?? '',
    numberOfItems: pickupInformation.numberOfItems,
    timeReady: pickupInformation.timeReady ?? undefined,
    additionalJobs: commonDetails.additionalJobs.map((job) => {
      if ((job || '').toUpperCase() === 'PRINTING') {
        return { type: 'PRINTING' as const, numberOfPages: printingPages };
      }
      return { type: 'OTHER' as const, numberOfPages: undefined as number | undefined };
    }),
    assignedPickupDriverId: pickupInformation.pickupDriverId ?? undefined,
    pickupDriverPhone: pickupInformation.pickupDriverPhone ?? undefined,
    pickupInstructions: pickupInformation.pickupInstructions ?? undefined,

    // Delivery Information
    deliveries: [{
      id: '1',
      personEntityName: deliveryInformation.deliveryName,
      attention: deliveryInformation.attention ?? undefined,
      phone: deliveryInformation.deliveryPhone ?? undefined,
      copyAddressFromClient: false,
      deliveryAddressLine1: deliveryAddr.line1,
      deliveryAddressLine2: deliveryAddr.line2 || undefined,
      deliveryCity: deliveryAddr.city,
      deliveryCounty: deliveryInformation.deliveryCountyId ? String(deliveryInformation.deliveryCountyId) : '',
      deliveryState: deliveryAddr.state,
      deliveryZip: deliveryAddr.zip,
      deliveryDate: deliveryDateTime,
      deliveryTime: commonDetails.deliveryTime ?? '12:00 PM',
      assignedDeliveryDriverId: deliveryInformation.deliveryDriverId ?? undefined,
      deliveryDriverPhone: deliveryInformation.deliveryDriverPhone ?? undefined,
      additionalDriverIds: [], // LIMITATION: API returns driver names, not IDs
      returnDate: returnDate,
      hasReturn: (!!returnDate) as any,
      deliveryInstructions: commonDetails.deliveryInstructions ?? '',
      hasDgrCheck: commonDetails.dgrCheck,
      checkNumber: commonDetails.checkNumber ?? undefined,
      checkAmount: commonDetails.checkAmount ?? undefined,
    }],

    // Billing
    deliveryBillings: [{
      baseCharge: getBillingAmount('BASE_CHARGE'),
      fuelSurcharge: getBillingAmount('FUEL_SURCHARGE'),
      boxFee: getBillingAmount('BOX_FEE'),
      copyCharges: getBillingAmount('COPY_CHARGES'),
      returnAmount: getBillingAmount('RETURN_CHARGE'), // Check backend code for return fee. "RETURN_CHARGE"?
      checkAdvancementFee: getBillingAmount('CHECK_ADVANCEMENT_FEE'),
      miscFee: (getBillingAmount('MISC_FEE') || commonDetails.miscFee || 0) as any,
      totalBillingFee: totalBillingAmount,
    }],
  };
};

export const DeliveryCreate = () => {
  const params = useParams<{ intakeId?: string; workOrderId?: string }>();
  const [searchParam, setSearchParam] = useSearchParams();
  const [submitAlertBoxOpen, setSubmitAlertBoxOpen] = useState(false);

  // Determine if in edit mode
  const isEditMode = !!(params.intakeId || params.workOrderId);
  const editId = params.intakeId ?? params.workOrderId;
  const isWorkOrderEdit = !!params.workOrderId;
  const schemas = React.useMemo(
    () => [
      DeliveryClientDetailsSchema,
      DeliveryPickupInformationSchema,
      buildDeliveryInformationSchema({ allowPastDeliveryDate: isEditMode }),
      buildDeliveryFormSchema({ allowPastDeliveryDate: isEditMode }),
    ],
    [isEditMode]
  );

  // Initialize steps - exclude billing step when editing work orders
  const [steps, setSteps] = useState(() => {
    const baseSteps = [
      { title: 'Client Details', isCompleted: false },
      { title: 'Pickup Information', isCompleted: false },
      { title: 'Delivery Information', isCompleted: false },
    ];
    // Only add billing step for intake forms, not for work order edits
    if (!isWorkOrderEdit) {
      baseSteps.push({ title: 'Billing', isCompleted: false });
    }
    return baseSteps;
  });
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const activeStepRef = React.useRef(activeStep);

  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  // Fetch existing data if in edit mode
  const { data: existingData, isLoading: isLoadingData, isError, error } = useQuery({
    queryKey: ['delivery-detail', editId],
    queryFn: async () => {
      if (!editId) return null;
      const result = await deliveryService.getDeliveryDetail(editId);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: isEditMode && !!editId,
  });

  const form = useForm<DeliveryFormData>({
    resolver: async (values, context, options) => {
      const schema = schemas[activeStepRef.current];
      return zodResolver(schema)(values, context, options);
    },
    mode: 'all',
    defaultValues: {
      type: 'DELIVERY',
      isPrepaid: false,
      pickupAddressSameAsClient: true,
      deliveries: [{
        id: '1',
        personEntityName: '',
        copyAddressFromClient: false,
        additionalDriverIds: [],
        hasDgrCheck: false,
        hasReturn: false,
      }] as any,
      deliveryBillings: [],
      pickupDate: undefined as unknown as Date,
    },
  });

  // Keep ref synced and trigger validation on step change
  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep, form]);

  const {
    getValues,
    reset,
    formState: { isDirty },
  } = form;

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const scrollToTop = () => {
    const scrollContainer = document.querySelector('.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToTop();
    }, 100);
  }, [activeStep]);

  useEffect(() => {
    setSearchParam({ stepper: steps[0].title }, { replace: true });

    // Load existing data in edit mode
    if (isEditMode && existingData) {
      const formData = transformApiResponseToFormData(existingData);
      reset(formData as DeliveryFormData);
    } else if (!isEditMode) {
      // Initialize with defaults for create mode only
      reset({
        type: 'DELIVERY',
        isPrepaid: false,
        pickupAddressSameAsClient: true,
        pickupDate: new Date(),
        deliveries: [],
        deliveryBillings: [],
      });
    }
    // Only run when existingData changes or when switching between create/edit modes

  }, [existingData, isEditMode]);

  useEffect(() => {
    const activeStepParam = searchParam.get('stepper');
    if (activeStepParam) {
      const index = steps.findIndex((step) => step.title === activeStepParam);
      if (index !== -1) {
        setActiveStep(index);
      }
    }
    // Only run when searchParam changes

  }, [searchParam]);

  const goToStep = async (step: number) => {
    const isValid = await form.trigger();
    if (isValid) {
      if (steps[step].isCompleted || step <= activeStep) {
        setSearchParam({ stepper: steps[step].title });
        setActiveStep(step);
      }
    }
  };

  const nextStep = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setSteps((prevSteps) =>
        prevSteps.map((step, index) => (index === activeStep ? { ...step, isCompleted: true } : step))
      );
      setSearchParam({ stepper: steps[activeStep + 1].title });
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setSearchParam({ stepper: steps[activeStep - 1].title });
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToTop();
    }, 100);
    // Only run when activeStep changes - form methods are stable

  }, [activeStep]);

  const createDeliveryMutation = useMutation({
    mutationFn: async (data: DeliveryCreateRequest) => {
      const result = await deliveryService.createDeliveryIntake(data);
      if (result.error) throw result.error;
      return result.data;
    },
  });

  const updateIntakeMutation = useMutation({
    mutationFn: async (data: EditIntakeFormRequest) => {
      if (!params.intakeId) throw new Error('Intake ID is required for update');
      const result = await deliveryService.updateDeliveryIntake(params.intakeId, data);
      if (result.error) throw result.error;
      return result.data;
    },
  });

  const updateWorkOrderMutation = useMutation({
    mutationFn: async (data: EditWorkOrderRequest) => {
      if (!params.workOrderId) throw new Error('Work Order ID is required for update');
      const result = await deliveryService.updateWorkOrder(params.workOrderId, data);
      if (result.error) throw result.error;
      return result.data;
    },
  });

  const saveHandler = async () => {
    const formData = getValues() as unknown as DeliveryFormData;

    // Transform additionalJobs from objects to string array and extract pages per API spec
    const additionalJobsArray = (formData.additionalJobs ?? []).map((job) => job.type);
    const printingJob = (formData.additionalJobs ?? []).find((job) => job.type === 'PRINTING');
    const pages = printingJob?.numberOfPages ?? undefined;

    if (!formData.deliveries || formData.deliveries.length === 0) {
      toast({
        variant: 'destructive',
        description: 'At least one delivery is required.',
        duration: 5000,
      });
      return;
    }

    const formatDateToAPIDate = (date: Date | string | undefined): string => {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const baseCommonData = {
      caseName: formData.caseName ?? undefined,
      fileReferenceNumber: formData.fileReferenceNumber ?? undefined,
      isPrepaid: formData.isPrepaid,
      pickupName: formData.pickupPersonName,
      pickupPhone: getPhoneNumberValue(formData.pickupPhone) ?? undefined,
      pickupAddress: `${formData.pickupAddressLine1}, ${formData.pickupAddressLine2 ? formData.pickupAddressLine2 + ', ' : ''}${formData.pickupCity}, ${formData.pickupState} ${formData.pickupZip}`,
      pickupCountyId: formData.pickupCounty || undefined,
      pickupDate: formatDateToAPIDate(formData.pickupDate),
      pickupType: formData.pickupType,
      pickupTypeOther: formData.pickupType === 'OTHER' && formData.pickupTypeOther?.trim() ? formData.pickupTypeOther.trim() : undefined,
      numberOfItems: Number(formData.numberOfItems),
      timeReady: formData.timeReady,
      additionalJobs: additionalJobsArray,
      pages,
      pickupDriverId: formData.assignedPickupDriverId || undefined,
      pickupInstructions: formData.pickupInstructions ?? undefined,
      assignedTeamId: undefined,
    };

    if (isEditMode) {
      const firstDelivery = formData.deliveries[0];
      const firstBilling = formData.deliveryBillings?.[0];

      const billingItems = [];
      if (firstBilling) {
        billingItems.push(
          { code: 'BASE_CHARGE', description: 'Base Charge', amount: firstBilling.baseCharge || 0, isAutoCalculated: true },
          { code: 'FUEL_SURCHARGE', description: 'Fuel Surcharge', amount: firstBilling.fuelSurcharge || 0, isAutoCalculated: true }
        );
        if (firstBilling.boxFee && firstBilling.boxFee > 0) {
          billingItems.push({ code: 'BOX_FEE', description: 'Box Fee', amount: firstBilling.boxFee, isAutoCalculated: true });
        }
        if (firstBilling.copyCharges && firstBilling.copyCharges > 0) {
          billingItems.push({ code: 'COPY_CHARGES', description: 'Copy Charges', amount: firstBilling.copyCharges, isAutoCalculated: true });
        }
        if (firstBilling.returnAmount && firstBilling.returnAmount > 0) {
          billingItems.push({ code: 'RETURN_AMOUNT', description: 'Return Amount', amount: firstBilling.returnAmount, isAutoCalculated: true });
        }
        if (firstBilling.checkAdvancementFee && firstBilling.checkAdvancementFee > 0) {
          billingItems.push({ code: 'CHECK_ADVANCEMENT_FEE', description: 'Check Advancement Fee', amount: firstBilling.checkAdvancementFee, isAutoCalculated: true });
        }
        if (firstBilling.miscFee && firstBilling.miscFee > 0) {
          billingItems.push({ code: 'MISC_FEE', description: 'Misc / Prepaid', amount: firstBilling.miscFee, isAutoCalculated: false });
        }
      }

      const editData: EditIntakeFormRequest | EditWorkOrderRequest = {
        ...baseCommonData,
        clientId: formData.clientId,
        clientContactId: formData.contactId,
        deliveryName: firstDelivery.personEntityName,
        attention: firstDelivery.attention ?? undefined,
        deliveryPhone: getPhoneNumberValue(firstDelivery.phone) ?? undefined,
        deliveryAddress: `${firstDelivery.deliveryAddressLine1}, ${firstDelivery.deliveryAddressLine2 ? firstDelivery.deliveryAddressLine2 + ', ' : ''}${firstDelivery.deliveryCity}, ${firstDelivery.deliveryState} ${firstDelivery.deliveryZip}`,
        deliveryCountyId: firstDelivery.deliveryCounty || undefined,
        deliveryDate: formatDateToAPIDate(firstDelivery.deliveryDate),
        deliveryTime: firstDelivery.deliveryTime,
        deliveryDriverId: firstDelivery.assignedDeliveryDriverId || undefined,
        additionalDeliveryDrivers: firstDelivery.additionalDriverIds ?? [],
        returnDate: firstDelivery.returnDate ? formatDateToAPIDate(firstDelivery.returnDate) : undefined,
        deliveryInstructions: firstDelivery.deliveryInstructions,
        dgrCheck: firstDelivery.hasDgrCheck || false,
        checkNumber: firstDelivery.hasDgrCheck ? (firstDelivery.checkNumber ?? undefined) : undefined,
        checkAmount: firstDelivery.hasDgrCheck ? (firstDelivery.checkAmount != null ? Number(firstDelivery.checkAmount) : undefined) : undefined,
        miscFee: firstBilling?.miscFee != null ? Number(firstBilling.miscFee) : undefined,
        ...(!isWorkOrderEdit && billingItems.length > 0 ? { billingItems } : {}),
      };

      if (isWorkOrderEdit) {
        updateWorkOrderMutation.mutate(editData as EditWorkOrderRequest, {
          onSuccess: () => {
            toast({
              variant: 'default',
              description: 'Delivery work order updated successfully.',
              duration: 5000,
            });
            navigate('/delivery');
          },
          onError: (error) => {
            displayError(error as unknown as GenericErrorResponse, 5000);
          },
        });
      } else {
        updateIntakeMutation.mutate(editData as EditIntakeFormRequest, {
          onSuccess: () => {
            toast({
              variant: 'default',
              description: 'Delivery intake form updated successfully.',
              duration: 5000,
            });
            navigate('/delivery');
          },
          onError: (error) => {
            displayError(error as unknown as GenericErrorResponse, 5000);
          },
        });
      }
    } else {
      try {
        for (let index = 0; index < formData.deliveries.length; index++) {
          const delivery = formData.deliveries[index];
          const billing = formData.deliveryBillings?.[index];
          const billingItems = [];

          if (billing) {
            billingItems.push(
              { code: 'BASE_CHARGE', description: 'Base Charge', amount: billing.baseCharge || 0, isAutoCalculated: true },
              { code: 'FUEL_SURCHARGE', description: 'Fuel Surcharge', amount: billing.fuelSurcharge || 0, isAutoCalculated: true }
            );
            if (billing.boxFee && billing.boxFee > 0) {
              billingItems.push({ code: 'BOX_FEE', description: 'Box Fee', amount: billing.boxFee, isAutoCalculated: true });
            }
            if (billing.copyCharges && billing.copyCharges > 0) {
              billingItems.push({ code: 'COPY_CHARGES', description: 'Copy Charges', amount: billing.copyCharges, isAutoCalculated: true });
            }
            if (billing.returnAmount && billing.returnAmount > 0) {
              billingItems.push({ code: 'RETURN_AMOUNT', description: 'Return Amount', amount: billing.returnAmount, isAutoCalculated: true });
            }
            if (billing.checkAdvancementFee && billing.checkAdvancementFee > 0) {
              billingItems.push({ code: 'CHECK_ADVANCEMENT_FEE', description: 'Check Advancement Fee', amount: billing.checkAdvancementFee, isAutoCalculated: true });
            }
            if (billing.miscFee && billing.miscFee > 0) {
              billingItems.push({ code: 'MISC_FEE', description: 'Misc / Prepaid', amount: billing.miscFee, isAutoCalculated: false });
            }
          }

          const requestData: DeliveryCreateRequest = {
            serviceType: 'DELIVERY',
            clientId: formData.clientId,
            clientContactId: formData.contactId,
            ...baseCommonData,
            deliveryName: delivery.personEntityName,
            attention: delivery.attention ?? undefined,
            deliveryPhone: getPhoneNumberValue(delivery.phone) ?? undefined,
            deliveryAddress: `${delivery.deliveryAddressLine1}, ${delivery.deliveryAddressLine2 ? delivery.deliveryAddressLine2 + ', ' : ''}${delivery.deliveryCity}, ${delivery.deliveryState} ${delivery.deliveryZip}`,
            deliveryCountyId: delivery.deliveryCounty || undefined,
            deliveryDate: formatDateToAPIDate(delivery.deliveryDate),
            deliveryTime: delivery.deliveryTime,
            deliveryDriverId: delivery.assignedDeliveryDriverId || undefined,
            additionalDeliveryDrivers: delivery.additionalDriverIds ?? [],
            returnDate: delivery.returnDate ? formatDateToAPIDate(delivery.returnDate) : undefined,
            deliveryInstructions: delivery.deliveryInstructions,
            dgrCheck: delivery.hasDgrCheck || false,
            checkNumber: delivery.hasDgrCheck ? (delivery.checkNumber ?? undefined) : undefined,
            checkAmount: delivery.hasDgrCheck ? (delivery.checkAmount != null ? Number(delivery.checkAmount) : undefined) : undefined,
            miscFee: billing?.miscFee != null ? Number(billing.miscFee) : undefined,
            billingItems,
            totalBillingAmount: billing?.totalBillingFee || 0,
          };

          await createDeliveryMutation.mutateAsync(requestData);
        }

        toast({
          variant: 'default',
          description: `Delivery service intake form${formData.deliveries.length > 1 ? 's' : ''} created successfully.`,
          duration: 5000,
        });
        navigate('/delivery');
      } catch (error) {
        displayError(error as unknown as GenericErrorResponse, 5000);
      }
    }
  };

  // Get page title
  const getPageTitle = () => {
    if (isEditMode && existingData) {
      const workOrderNumber = existingData.commonDetails?.workOrderNumber;
      if (workOrderNumber) {
        return `Edit: W/O: ${workOrderNumber}`;
      }
      return 'Edit: Delivery Intake Form';
    }
    return 'Delivery Service Entry';
  };

  // Show loading state while fetching data in edit mode
  if (isEditMode && isLoadingData) {
    return (
      <div className="w-full scrollbar-hide">
        <div className="w-full">
          <ToolBar title="Loading..." />
          <div className="mt-8 px-6 flex justify-center items-center h-64">
            <p className="text-gray-500 text-lg">Loading delivery details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if data loading failed in edit mode
  if (isEditMode && isError) {
    return (
      <div className="w-full scrollbar-hide">
        <div className="w-full">
          <ToolBar title="Error Loading Data" />
          <div className="mt-8 px-6 flex flex-col justify-center items-center h-64">
            <p className="text-red-500 text-lg mb-4">Failed to load delivery details</p>
            <p className="text-gray-600 mb-4">{error?.message || 'Unknown error occurred'}</p>
            <Button onClick={() => navigate('/delivery')}>
              Back to List
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show error if in edit mode but no data loaded
  if (isEditMode && !existingData && !isLoadingData) {
    return (
      <div className="w-full scrollbar-hide">
        <div className="w-full">
          <ToolBar title="Data Not Found" />
          <div className="mt-8 px-6 flex flex-col justify-center items-center h-64">
            <p className="text-red-500 text-lg mb-4">Delivery record not found</p>
            <Button onClick={() => navigate('/delivery')}>
              Back to List
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full scrollbar-hide">
      <div className="w-full">
        <ToolBar title={getPageTitle()} />
        <div className="mt-3 flex justify-center pl-24">
          <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} />
        </div>

        <FormProvider {...form}>
          {activeStep === 0 && <DeliveryClientDetails />}
          {activeStep === 1 && <DeliveryPickupInformation allowPastDates={isEditMode} />}
          {activeStep === 2 && <DeliveryInformation allowPastDates={isEditMode} />}
          {activeStep === 3 && !isWorkOrderEdit && <DeliveryBilling isEditMode={isEditMode} />}
        </FormProvider>

        <div className={`flex justify-end mt-4 mb-8 w-full gap-2`}>
          {activeStep > 0 && (
            <Button
              variant="outline"
              className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
              onClick={prevStep}
            >
              Back
            </Button>
          )}
          {activeStep !== steps.length - 1 && (
            <Button
              className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white disabled:bg-primary"
              onClick={nextStep}
              disabled={activeStep === steps.length - 1}
            >
              Next
            </Button>
          )}

          {activeStep === steps.length - 1 && (
            <div className="flex flex-col items-center">
              <AlertBox
                open={submitAlertBoxOpen}
                triggerButton={
                  <Button
                    variant="outline"
                    className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
                    onClick={async () => {
                      const isValid = await form.trigger();
                      if (isValid) {
                        setSubmitAlertBoxOpen(true);
                      }
                    }}
                    disabled={!form.formState.isValid || createDeliveryMutation.isPending || updateIntakeMutation.isPending || updateWorkOrderMutation.isPending}
                  >
                    Save
                  </Button>
                }
                title={isEditMode ? 'Update Delivery' : 'Save Delivery Service'}
                description={
                  isEditMode
                    ? `Are you sure you want to update this ${isWorkOrderEdit ? 'work order' : 'intake form'}?`
                    : 'Are you sure you want to save this delivery service intake form as Draft?'
                }
                confirmBtnClick={saveHandler}
                confirmBtnLabel={isEditMode ? 'Update' : 'Save'}
                cancelBtnLabel="Cancel"
                isLoading={createDeliveryMutation.isPending || updateIntakeMutation.isPending || updateWorkOrderMutation.isPending}
                onOpenChange={setSubmitAlertBoxOpen}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
